import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { PasswordField } from "@/components/auth/PasswordField";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { runPendingSaveAfterAuth, getPendingSave } from "@/lib/pendingSave";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [legalRequirements, setLegalRequirements] = useState(null);
  const [legalError, setLegalError] = useState("");
  const [acceptedOffer, setAcceptedOffer] = useState(false);
  const [privacyAcknowledged, setPrivacyAcknowledged] = useState(false);
  const [ruConsent, setRuConsent] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/auth/registration-requirements")
      .then(({ data }) => {
        if (!active) return;
        if (!["RU", "INTERNATIONAL"].includes(data?.jurisdiction)
            || !data?.offer_version || !data?.privacy_version
            || typeof data?.requires_ru_personal_data_consent !== "boolean") {
          throw new Error("Региональные условия регистрации недоступны.");
        }
        setLegalRequirements(data);
        setLegalError("");
      })
      .catch(() => {
        if (active) setLegalError("Не удалось загрузить условия регистрации. Обновите страницу.");
      });
    return () => { active = false; };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!legalRequirements || !acceptedOffer || !privacyAcknowledged
        || (legalRequirements.requires_ru_personal_data_consent && !ruConsent)) {
      toast.error("Подтвердите обязательные условия регистрации.");
      return;
    }
    if (password.length < 6) {
      toast.error("Пароль должен быть не короче 6 символов");
      return;
    }
    setLoading(true);
    const res = await register(email, password, name, {
      accepted_offer: acceptedOffer,
      privacy_acknowledged: privacyAcknowledged,
      ru_personal_data_consent: legalRequirements.requires_ru_personal_data_consent && ruConsent,
    });
    setLoading(false);
    if (res.ok) {
      toast.success("Аккаунт создан");
      const pending = getPendingSave();
      await runPendingSaveAfterAuth();
      // Return-path handoff (e.g. from /master-order-testing when opened anonymously).
      let postLogin = null;
      try {
        postLogin = sessionStorage.getItem("post_login_redirect");
        if (postLogin) sessionStorage.removeItem("post_login_redirect");
      } catch {}
      if (postLogin && postLogin.startsWith("/") && !postLogin.startsWith("//")) {
        nav(postLogin, { replace: true });
      } else if (pending) {
        nav(`/library`, { replace: true });
      } else {
        nav("/hall", { replace: true });
      }
    } else {
      toast.error(res.error || "Не удалось зарегистрироваться");
    }
  };

  return (
    <div className="min-h-[calc(100dvh-72px)] sm:min-h-[calc(100dvh-44px)] flex flex-col bg-cream" data-testid="register-viewport">
      <div className="flex-1 flex flex-col justify-center px-7 py-10 max-w-md w-full mx-auto">
        <div className="mb-8">
          <div className="chip bg-sand/60 text-espresso inline-block mb-3">Anokina Studio</div>
          <h1 className="font-serif text-4xl leading-tight text-espresso">Создать аккаунт</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Создайте аккаунт, чтобы читать библиотеку и пользоваться возможностями Anokina Studio.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Имя</label>
            <Input
              data-testid="register-name-input"
              name="name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 h-12 rounded-2xl"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Email</label>
            <Input
              data-testid="register-email-input"
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 h-12 rounded-2xl"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Пароль</label>
            <PasswordField
              data-testid="register-password-input"
              toggleTestId="register-password-toggle"
              name="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-12 rounded-2xl"
            />
          </div>
          <div className="space-y-3 rounded-2xl border border-espresso/15 bg-white/65 px-4 py-4 text-sm text-espresso"
            data-testid="registration-legal-confirmations">
            <p className="text-xs text-muted-foreground">
              {legalRequirements
                ? "Условия регистрации — " + (legalRequirements.jurisdiction === "RU" ? "российский" : "международный") + " контур"
                : "Проверяем условия регистрации…"}
            </p>
            {legalError && <p role="alert" className="text-sm text-red-700">{legalError}</p>}
            <label className="flex items-start gap-3">
              <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-espresso"
                data-testid="register-offer-consent"
                checked={acceptedOffer} onChange={(e) => setAcceptedOffer(e.target.checked)}
                disabled={!legalRequirements} required />
              <span>Я принимаю условия <Link to="/legal/offer" target="_blank" rel="noopener noreferrer"
                className="underline underline-offset-4">Публичной оферты</Link>
                {legalRequirements ? " (ред. " + legalRequirements.offer_version + ")" : ""}.</span>
            </label>
            <label className="flex items-start gap-3">
              <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-espresso"
                data-testid="register-privacy-ack"
                checked={privacyAcknowledged} onChange={(e) => setPrivacyAcknowledged(e.target.checked)}
                disabled={!legalRequirements} required />
              <span>Я ознакомилась/ознакомился с <Link to="/legal/privacy" target="_blank" rel="noopener noreferrer"
                className="underline underline-offset-4">Политикой конфиденциальности</Link>
                {legalRequirements ? " (ред. " + legalRequirements.privacy_version + ")" : ""}.</span>
            </label>
            {legalRequirements?.requires_ru_personal_data_consent && (
              <label className="flex items-start gap-3">
                <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-espresso"
                  data-testid="register-ru-data-consent"
                  checked={ruConsent} onChange={(e) => setRuConsent(e.target.checked)}
                  required />
                <span>Я даю отдельное <Link to="/legal/ru-personal-data-consent"
                  target="_blank" rel="noopener noreferrer"
                  className="underline underline-offset-4">согласие на обработку персональных данных</Link>
                  {" (ред. " + legalRequirements.ru_personal_data_version + ")"}.</span>
              </label>
            )}
            <p className="text-xs text-muted-foreground">
              Регистрация не означает согласия на рекламную рассылку или публичное размещение профиля.
            </p>
          </div>
          <Button
            data-testid="register-submit-button"
            type="submit"
            disabled={loading || !legalRequirements || !acceptedOffer || !privacyAcknowledged
              || (legalRequirements.requires_ru_personal_data_consent && !ruConsent)}
            className="btn-pill w-full h-12 bg-espresso hover:bg-espressoDark text-white text-base"
          >
            {loading ? "Создаём…" : "Создать аккаунт"}
          </Button>
        </form>

        <p className="text-sm text-center mt-6 text-muted-foreground">
          Уже есть аккаунт?{" "}
          <Link data-testid="link-to-login" to="/login" className="text-espresso font-medium underline underline-offset-4">
            Войти
          </Link>
        </p>
      </div>
    </div>
  );
}
